"use client";

// Saved / reacted / following lists (Watching lives in watching.ts).
// Anonymous visitors keep them in localStorage; once signed in, LibrarySync pulls
// the account's lists from /api/me/library, merges anything collected while
// logged out, and every toggle is mirrored to the server.

import { useCallback, useSyncExternalStore } from "react";
import type { ProfileRef, VideoSummary } from "@/lib/api/types";
import { clearLocalWatching } from "@/lib/watching";

export type VideoList = "saved" | "reacted";

export interface Lists {
  saved: VideoSummary[];
  reacted: VideoSummary[];
  following: ProfileRef[];
}
type ListName = keyof Lists;

const NAMES: ListName[] = ["saved", "reacted", "following"];
const EMPTY: Lists = { saved: [], reacted: [], following: [] };
/** Check Later was folded into Saved; its old local copy moves over once. */
const LEGACY_LATER = "sweet.later";
const key = (name: ListName) => `sweet.${name}`;
const cache = new Map<ListName, unknown[]>();
const listeners = new Set<() => void>();
let remote = false;

function read<N extends ListName>(name: N): Lists[N] {
  if (cache.has(name)) return cache.get(name) as Lists[N];
  let value: Lists[N] = EMPTY[name];
  try {
    const raw = window.localStorage.getItem(key(name));
    if (raw) value = JSON.parse(raw);
  } catch {
    // Storage blocked (private mode, sandboxed preview): fall back to empty lists.
  }
  cache.set(name, value);
  return value;
}

function write<N extends ListName>(name: N, value: Lists[N]) {
  cache.set(name, value);
  try {
    window.localStorage.setItem(key(name), JSON.stringify(value));
  } catch {
    // Keep the in-memory copy so the UI still reacts this session.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (!e.key?.startsWith("sweet.")) return;
    cache.clear();
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function push(body: unknown) {
  return fetch("/api/me/library", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
}

function mirror(list: ListName, id: string, on: boolean) {
  if (remote) void push({ op: "toggle", list, id, on }).catch(() => {});
}

export function useList<N extends ListName>(name: N): Lists[N] {
  return useSyncExternalStore(
    subscribe,
    () => read(name),
    () => EMPTY[name],
  );
}

export function useVideoToggle(name: VideoList, video: VideoSummary) {
  const list = useList(name);
  const active = list.some((v) => v.id === video.id);
  const toggle = useCallback(() => {
    const current = read(name);
    const on = !current.some((v) => v.id === video.id);
    write(name, on ? [video, ...current].slice(0, 500) : current.filter((v) => v.id !== video.id));
    mirror(name, video.id, on);
  }, [name, video]);
  return [active, toggle] as const;
}

export function useFollow(profile: ProfileRef) {
  const list = useList("following");
  const active = list.some((p) => p.slug === profile.slug);
  const toggle = useCallback(() => {
    const current = read("following");
    const on = !current.some((p) => p.slug === profile.slug);
    write(
      "following",
      on
        ? [{ slug: profile.slug, name: profile.name, kind: profile.kind, type: profile.type ?? null, avatar: profile.avatar, verified: profile.verified }, ...current]
        : current.filter((p) => p.slug !== profile.slug),
    );
    mirror("following", profile.slug, on);
  }, [profile]);
  return [active, toggle] as const;
}

const noop = () => () => {};

/** False during SSR/hydration, true afterwards; avoids flashing empty states for stored lists. */
export function useHydrated() {
  return useSyncExternalStore(
    noop,
    () => true,
    () => false,
  );
}

export function clearList(name: ListName) {
  const ids = read(name).map((x) => ("id" in x ? x.id : x.slug));
  write(name, EMPTY[name]);
  if (remote) void push({ op: "clear", list: name, ids }).catch(() => {});
}

/** Wipes local copies (on sign-out), so the next person on this device starts clean. */
export function clearAllLists() {
  remote = false;
  for (const name of NAMES) write(name, EMPTY[name]);
  clearLocalWatching();
}

function adoptLegacyLater() {
  try {
    const raw = window.localStorage.getItem(LEGACY_LATER);
    if (raw === null) return;
    const later: VideoSummary[] = JSON.parse(raw);
    const saved = read("saved");
    if (Array.isArray(later)) write("saved", [...saved, ...later.filter((v) => v?.id && !saved.some((s) => s.id === v.id))].slice(0, 500));
    window.localStorage.removeItem(LEGACY_LATER);
  } catch {
    // Storage blocked or corrupt: nothing to move.
  }
}

/** Adopts the server lists, uploading anything collected while signed out. Resolves to whether the visitor is signed in. */
export async function syncWithServer(): Promise<boolean> {
  adoptLegacyLater();
  const res = await fetch("/api/me/library", { cache: "no-store" });
  const data: (Lists & { authenticated: true }) | { authenticated: false } = res.ok ? await res.json() : { authenticated: false };
  if (!data.authenticated) {
    remote = false;
    return false;
  }
  remote = true;
  let server: Lists = data;
  const missing = {
    saved: read("saved").filter((v) => !server.saved.some((s) => s.id === v.id)).map((v) => v.id),
    reacted: read("reacted").filter((v) => !server.reacted.some((s) => s.id === v.id)).map((v) => v.id),
    following: read("following").filter((p) => !server.following.some((s) => s.slug === p.slug)).map((p) => p.slug),
  };
  if (Object.values(missing).some((l) => l.length)) {
    const merged = await push({ op: "merge", ...missing });
    if (merged.ok) server = await merged.json();
  }
  for (const name of NAMES) write(name, server[name] as never);
  return true;
}
